import { useHistory, useRouteParam } from '@core/router';
import { classnames } from '@core/utils/css';
import { R } from '@core/utils/r';
import React, { Component, ExoticComponent, memo, ReactElement, Suspense, useEffect, useMemo, useState } from 'react';
import { Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import { useIsPhone } from '@core/utils/hooks/dom';

export interface TabsMapping {
  title: string;
  leftIcon?: string;
  rightIcon?: string;
  leftHTML?: React.ReactNode;
  rightHTML?: React.ReactNode;
  component: Component<any> | ExoticComponent<any>;
  props?: any;
  route?: string;
  visible?: boolean;
}

export interface TabsProps {
  vertical?: boolean;
  tabs: TabsMapping[];
  initialIndex?: number;
  activeIndex?: number;
  pills?: boolean;
  simple?: boolean;
  routeParam?: string;
  route?(tab: string): string;
  isModal?: boolean;
  isLazy?: boolean;
  onChange?(index: number): void;
  children?: React.ReactNode;
}

export const Tabs = memo((props: TabsProps) => {
  const { vertical = false, pills = false, simple = false, isModal = false, isLazy = false } = props;
  const tabs = useMemo(() => props.tabs.filter(t => t.visible !== false), [props.tabs]);

  let routeTabParam = '';
  if (!isModal && props.routeParam) {
    routeTabParam = useRouteParam(props.routeParam || 'randomRouteParam', true);
  }
  const history = useHistory();

  const initialIndex = useMemo(() => {
    if (props.initialIndex) {
      return props.initialIndex;
    } else if (props.routeParam && routeTabParam) {
      return tabs.findIndex(t => t.route === routeTabParam) || 0;
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialIndex]);

  const [activeTabIndex, setActiveTabIndex] = useState<number>(initialIndex);

  useEffect(() => {
    if (!R.isUndefined(props.activeIndex)) {
      setActiveTabIndex(props.activeIndex);
    }
  }, [props.activeIndex]);

  // On phones the tab list is a sideways-scrolling strip; keep the active tab in view.
  const isPhone = useIsPhone();
  const navId = useMemo(() => `gc-tabs-${Math.random().toString(36).slice(2, 9)}`, []);
  useEffect(() => {
    if (!isPhone) return;
    const nav = document.getElementById(navId);
    const active = nav?.querySelector<HTMLElement>('.nav-link.active');
    if (!nav || !active) return;
    const navRect = nav.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    nav.scrollLeft += activeRect.left - navRect.left - (navRect.width - activeRect.width) / 2;
  }, [isPhone, activeTabIndex, navId]);

  useEffect(() => {
    const tab = tabs[activeTabIndex];
    if (props.route && tab && tab.route && props.routeParam) {
      const url = props.route(tab.route);
      history.replace(url);
    }
    props.onChange && props.onChange(activeTabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex, props.onChange]);

  const navClasses = useMemo(() => {
    const items = ['gc-tabs-nav'];
    if (pills) {
      items.push('navtab-bg');
    } else {
      if (!simple) {
        items.push('nav-tabs-custom');
      }
    }
    if (vertical) {
      items.push('flex-column');
    }
    return items;
  }, [pills, simple, vertical]);

  const navBar = (
    <Nav id={navId} pills={pills} tabs={!pills} className={classnames(navClasses)}>
      {tabs.map((tab, index) => (
        <NavItem key={`tab-${index}`}>
          <NavLink
            style={{ cursor: 'pointer' }}
            className={classnames({
              active: activeTabIndex === index,
            })}
            onClick={() => setActiveTabIndex(index)}
          >
            {tab.leftIcon && <i className={tab.leftIcon}></i>}
            {tab.leftHTML}
            <span className={`${tab.leftIcon || tab.rightIcon ? 'd-sm-block' : ''} `}>{tab.title}</span>
            {tab.rightHTML}
            {tab.rightIcon && <i className={tab.rightIcon}></i>}
          </NavLink>
        </NavItem>
      ))}
      {!!props.children && props.children}
    </Nav>
  );

  const lazyPanes = useMemo(() => new Map<number, ReactElement>(), []);

  const panes = useMemo(
    () => (
      <TabContent activeTab={activeTabIndex}>
        {tabs.map((tab, index) => {
          const TabComponent = tab.component as any;
          if (isLazy) {
            if (activeTabIndex === index && !lazyPanes.get(index)) {
              lazyPanes.set(
                index,
                <TabPane tabId={index} className={vertical ? '' : 'py-3'} key={`pane-${index}`}>
                  <TabComponent key={index} {...tab.props} />
                </TabPane>,
              );
            }
            return lazyPanes.get(index);
          }

          return (
            <TabPane tabId={index} className={vertical ? '' : 'py-3'} key={`pane-${index}`}>
              <TabComponent key={index} {...tab.props} />
            </TabPane>
          );
        })}
      </TabContent>
    ),
    [activeTabIndex, isLazy, lazyPanes, tabs, vertical],
  );

  if (vertical) {
    return (
      <Row>
        <Col lg={2}>{navBar}</Col>
        <Col lg={10}>{panes}</Col>
      </Row>
    );
  } else if (isLazy) {
    return (
      <React.Fragment>
        {navBar}
        {panes}
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        {navBar}

        {panes}
      </React.Fragment>
    );
  }
});
